import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import { ConfirmDialog } from "primereact/confirmdialog";

import {
  EmailPreviewType,
  FirstChargeEmailResponse,
  GoLiveAttempt,
  GoLiveResponse,
  ProspectDetail,
  ProspectStation,
  SaveStationCredentialsPayload,
  StationEvent,
  VendorConfig,
} from "../../../../types/onboarding";
import {
  canResendGoLiveEmail,
  canSendConfigEmail,
  isConfigEmailSent,
  isStationConfigStage,
  PublishAction,
  publishActionFor,
  stateLabel,
} from "../../../../utils/onboardingStates";
import {
  attemptOutcomeLabel,
  attemptOutcomeSeverity,
  attemptSourceLabel,
  firstChargeEmailNeedsAttention,
  hasFirstCharge,
  lastFirstChargeAttempt,
} from "../../../../utils/firstCharge";
import {
  stationEventLabel,
  stationStatusLabel,
  stationStatusSeverity,
} from "../../../../utils/stations";
import {
  GENERIC_VENDOR_ID,
  vendorForManufacturer,
} from "../../../../utils/vendorConfigs";
import { actorLabel } from "../../../../utils/actors";
import {
  isStaleStationIdentity,
  staleIdentityCount,
} from "../../../../utils/stationIdentity";
import {
  needsWallboxClarification,
  stationCountLabel,
  stationCounts,
} from "../../../../utils/stationCounts";
import {
  availabilityProgress,
  needsPublishWarning,
  readinessLabel,
  readinessSeverity,
  readyDuration,
} from "../../../../utils/stationReadiness";
import { NextStepAction } from "../../../../utils/prospectNextStep";
import StationCredentialsDialog from "./StationCredentialsDialog";
import GoLiveDialog from "./GoLiveDialog";
import EmailPreviewDialog from "./EmailPreviewDialog";
import ProvisioningCell from "./ProvisioningCell";
import ProvisioningDialog from "./ProvisioningDialog";
import useStationProvisioning, { StationProvisioning } from "../../../../hooks/useStationProvisioning";
import { isOnboardingEnabled } from "../../../../config/featureFlags";
import {
  firstChargeEmailErrorMessage,
  goLiveErrorMessage,
  sendConfigEmailErrorMessage,
} from "./stationConfigErrors";

interface StationConfigPanelProps {
  prospect: ProspectDetail;
  stations: {
    stations: ProspectStation[];
    allCredentialsSaved: boolean;
    vendorConfigs: VendorConfig[];
    loading: boolean;
    error: boolean;
    saveCredentials: (
      stationId: string,
      payload: SaveStationCredentialsPayload
    ) => Promise<boolean>;
    revealKey: (stationId: string) => Promise<string | null>;
    registerStations: () => Promise<boolean>;
    promote: () => Promise<boolean>;
    sendEmail: () => Promise<boolean>;
    markLive: (
      stationIds?: string[],
      resend?: boolean,
      action?: PublishAction
    ) => Promise<GoLiveResponse | null>;
    resendFirstChargeEmail: () => Promise<FirstChargeEmailResponse | null>;
  };
  // Newest recorded go-live attempt, or null when there is none. An attempt —
  // not proof the customer received anything.
  lastGoLiveAttempt?: GoLiveAttempt | null;
  // The page's read of each station's eRound setup, shared with the next-step
  // bar; the panel reads its own when it is not given one.
  provisioning?: { byStation: Record<string, StationProvisioning>; reload: () => Promise<unknown> | void };
  // Set when the next-step button in the header asks for this panel's action.
  requestedAction?: NextStepAction | null;
  // The station a requested eRound review is for.
  requestedStationId?: string | null;
  onRequestHandled?: () => void;
}

// One send action awaiting confirmation in the email preview (CWA-173).
interface EmailPreviewRequest {
  type: EmailPreviewType;
  header: string;
  intro: string;
  sendLabel: string;
  sendIcon?: string;
  onSend: () => Promise<unknown>;
  errorMessage: (err: any) => string;
  blockOnErrors?: boolean;
}

const formatDate = (iso?: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

const formatActor = (actor?: string | null): string =>
  actorLabel(actor) || "—";

/**
 * Station configuration panel on the prospect detail page (CWA-91, CWA-94).
 *
 * Shown from "contract signed" onwards. Lists every station of the prospect
 * with the access data the customer needs, and offers exactly the progression
 * actions the backend accepts at the current stage.
 *
 * Access data is still generated automatically when station IDs are assigned, so
 * correcting it by hand stays a recovery path. The access key is not shown until
 * an agent asks for it on a station row: ops has to type it into eRound, and the
 * customer receives the same key by e-mail, so every reveal is fetched on demand
 * and audited rather than rendered with the list.
 *
 * Registering the stations and promoting the customer are NOT automatic. They
 * used to fire on their own when a contract was signed; the backend removed
 * that (acc-management f921e41), so ops now walks the prospect through
 * "stations ready" and "promote" itself before any configuration email exists
 * to send.
 *
 * The final step is marking the station live, which also sends the customer
 * their confirmation email. Going live is a property of the PROSPECT: the
 * backend derives every station's status from it, so this panel never suggests
 * one station can go live on its own.
 */
const StationConfigPanel: React.FC<StationConfigPanelProps> = ({
  prospect,
  stations: model,
  lastGoLiveAttempt = null,
  provisioning: shared,
  requestedAction = null,
  requestedStationId = null,
  onRequestHandled,
}) => {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<ProspectStation | null>(null);
  const [goLiveOpen, setGoLiveOpen] = useState(false);
  // Kept after closing so the dialog keeps its content while it animates out.
  const [emailRequest, setEmailRequest] = useState<EmailPreviewRequest | null>(
    null
  );
  const [emailOpen, setEmailOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState<any>(null);
  // Keys an agent has asked to see, per station. Held only for this render of
  // the page: navigating away or reloading hides them again.
  const [shownKeys, setShownKeys] = useState<Record<string, string>>({});
  const [revealing, setRevealing] = useState<string | null>(null);

  const state = prospect.current_state;

  const {
    stations,
    allCredentialsSaved,
    vendorConfigs,
    loading,
    error,
    saveCredentials,
    revealKey,
    sendEmail,
    markLive,
    resendFirstChargeEmail,
  } = model;

  // eRound provisioning (SOPS-148) is part of onboarding.
  const provisioningShown = isOnboardingEnabled;
  const ownsProvisioning = provisioningShown && !shared;
  const own = useStationProvisioning(
    ownsProvisioning ? stations.map((s) => s.station_id) : [],
    ownsProvisioning ? state : null
  );
  const provisioning = shared ?? own;
  const [reviewing, setReviewing] = useState<string | null>(null);
  const reviewed = reviewing ? provisioning.byStation[reviewing] : undefined;

  const hasStations = stations.length > 0;
  const staleCount = staleIdentityCount(stations);
  const isMultiStation = stations.length > 1;
  // Every bit of go-live copy agrees with how many stations the prospect has.
  const stationWord = isMultiStation ? "stations" : "station";
  const vendor = vendorForManufacturer(vendorConfigs, prospect.wallbox_manufacturer);
  const isGenericFallback =
    !!vendor &&
    vendor.vendor_id?.toUpperCase() === GENERIC_VENDOR_ID &&
    (prospect.wallbox_manufacturer ?? "").trim().toUpperCase() !==
      GENERIC_VENDOR_ID;

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  // Every action that emails the customer is confirmed in front of the email
  // itself (CWA-173). Send runs the same action the old confirmation ran.
  const previewThenSend = (request: EmailPreviewRequest) => {
    setEmailRequest(request);
    setEmailOpen(true);
  };

  const confirmSend = () => {
    const resend = isConfigEmailSent(state);
    previewThenSend({
      type: "CONFIGURATION",
      header: resend ? "Resend configuration email" : "Send configuration email",
      intro: `The configuration email will be sent to ${prospect.email}. It contains the station access data and the setup files for ${
        vendor?.vendor_name ?? "this manufacturer"
      }. Access keys are hidden in this preview; the customer receives them.`,
      sendLabel: resend ? "Resend" : "Send",
      onSend: () => run(sendEmail),
      errorMessage: sendConfigEmailErrorMessage,
    });
  };

  // The same operation under the name that matches what the agent is doing: at
  // the ready-to-publish stage they are confirming a publication they have just
  // made in eRound; earlier, they are deciding the station is up (research R9).
  const publishAction = publishActionFor(state);
  const isConfirming = publishAction === "confirm-publication";
  const publishLabel = isConfirming
    ? "Confirm publication"
    : `Mark ${stationWord} as live`;

  // eRound may still have the location private, in which case confirming here
  // records something the customer cannot actually see. Advisory only: an agent
  // who knows better may proceed.
  const privateInEround = needsPublishWarning(prospect);
  const privateWarning = privateInEround
    ? ` eRound still has this location private, so publish it there first — otherwise the ${stationWord} will not be visible to drivers.`
    : "";

  // Single-station prospects have nothing to choose, so they skip the picker.
  const startGoLive = () => {
    if (isMultiStation) {
      setGoLiveOpen(true);
      return;
    }
    previewThenSend({
      type: "GO_LIVE",
      header: publishLabel,
      // The Back Office publishes nothing — a location becomes visible to
      // drivers when an agent makes it public in eRound. Claiming otherwise sent
      // agents here expecting this to do the publishing.
      intro: `Confirming this says you have already made this location public in eRound. The Back Office does not publish it — it records the ${stationWord} as live and sends the confirmation email below to ${prospect.email}.${privateWarning}`,
      sendLabel: isConfirming ? "Confirm" : "Mark as live",
      sendIcon: "pi pi-bolt",
      onSend: () =>
        run(() => markLive(undefined, false, publishAction ?? "go-live")),
      errorMessage: goLiveErrorMessage,
      // Going live never waits on its email — a failed one is reported after
      // the fact — so a problem in the preview is shown but does not block.
      blockOnErrors: false,
    });
  };

  const confirmResend = () => {
    previewThenSend({
      type: "GO_LIVE",
      header: "Send confirmation email again",
      intro: `The confirmation email will be sent to ${prospect.email} again. The customer's ${stationWord} stay${isMultiStation ? "" : "s"} live either way.`,
      sendLabel: "Send again",
      onSend: () => run(() => markLive(undefined, true)),
      errorMessage: goLiveErrorMessage,
    });
  };

  // Short values on purpose: the column header already says "Access data", and
  // spelling it out again collided word-for-word with the activity entry below.
  // Every press sends another e-mail — the backend applies no duplicate guard on
  // this action — so the confirmation says so rather than implying one send.
  const confirmResendFirstCharge = () => {
    const again = !!lastFirstChargeAttempt(prospect);
    previewThenSend({
      type: "FIRST_CHARGE",
      header: again ? "Send welcome email again" : "Send welcome email",
      intro: `${
        again ? "Another welcome email" : "The welcome email"
      } will be sent to ${
        prospect.email
      }, confirming their first charging session and how their billing is handled.`,
      sendLabel: again ? "Send again" : "Send",
      onSend: () => run(resendFirstChargeEmail),
      errorMessage: firstChargeEmailErrorMessage,
    });
  };

  const identityBody = (station: ProspectStation) => {
    if (!isStaleStationIdentity(station)) return station.station_id;
    return (
      <span className="flex align-items-center gap-2 flex-wrap">
        <span>{station.station_id}</span>
        <Tag
          severity="warning"
          value="Needs re-saving"
          // The stored value is named rather than judged: a station really can
          // be registered in eRound under a different technical name, so this
          // says what disagrees and leaves the decision to the agent.
          title={`This station is stored as "${station.charge_point_id}", which eRound will not match. Re-save its access data to correct it.`}
        />
      </span>
    );
  };

  const lifecycleBody = (station: ProspectStation) => (
    <Tag
      severity={stationStatusSeverity(station)}
      value={stationStatusLabel(station)}
    />
  );

  const actionsBody = (station: ProspectStation) => (
    <Button
      label="Correct"
      icon="pi pi-pencil"
      text
      size="small"
      aria-label={`Correct access data for ${station.station_id}`}
      onClick={() => setEditing(station)}
    />
  );

  const showKey = async (stationId: string) => {
    setRevealing(stationId);
    try {
      const key = await revealKey(stationId);
      if (key) setShownKeys((prev) => ({ ...prev, [stationId]: key }));
    } finally {
      setRevealing(null);
    }
  };

  // The access key an agent types into eRound, offered on the station's own row:
  // it is the reason the row is read at all, so it no longer sits behind the
  // expander. The key itself is still fetched only when asked for, one station
  // at a time, and every reveal is recorded on the customer's history.
  const accessKeyBody = (station: ProspectStation) => {
    if (!station.has_credentials) {
      return <Tag severity="warning" value="Missing" />;
    }

    const shown = shownKeys[station.station_id];
    if (shown) {
      return (
        <span
          className="font-medium text-sm"
          style={{ wordBreak: "break-all", userSelect: "all" }}
          data-testid={`access-key-${station.station_id}`}
        >
          {shown}
        </span>
      );
    }

    return (
      <Button
        label="Show access key"
        icon="pi pi-eye"
        text
        size="small"
        className="p-0"
        loading={revealing === station.station_id}
        disabled={revealing === station.station_id}
        aria-label={`Show access key for ${station.station_id}`}
        onClick={() => showKey(station.station_id)}
      />
    );
  };

  // Only the entry kinds this screen has words for are shown; anything else is
  // dropped rather than printed as an identifier.
  const activityBody = (station: ProspectStation) => {
    const seen: Record<string, number> = {};
    const entries = [...(station.events ?? [])]
      .sort(
        (a, b) =>
          new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime()
      )
      .map((event: StationEvent) => {
        // Counted in chronological order so the first "went live" reads as the
        // go-live and every later one as what it actually was: a resend.
        const occurrence = (seen[event.type] = (seen[event.type] ?? 0) + 1);
        return {
          ...event,
          label: stationEventLabel(event.type, occurrence > 1),
        };
      })
      .filter((event) => !!event.label);

    if (!entries.length) {
      return <div className="text-500 text-sm p-2">Nothing has happened yet.</div>;
    }

    return (
      <ul className="list-none p-2 m-0">
        {entries.map((event, i) => (
          <li key={`${event.type}-${event.occurred_at}-${i}`} className="mb-1">
            <span className="font-medium">{event.label}</span>{" "}
            <span className="text-500 text-sm">
              {formatDate(event.occurred_at)} · {formatActor(event.by)}
            </span>
          </li>
        ))}
      </ul>
    );
  };

  // The header's next-step button hands over to this panel rather than carrying
  // the action itself: every one of these is confirmed in front of the station
  // list it applies to. Runs once per request, then hands the request back.
  useEffect(() => {
    if (!requestedAction) return;
    if (requestedAction === "review-eround" && requestedStationId) setReviewing(requestedStationId);
    if (requestedAction === "send-config-email") confirmSend();
    if (requestedAction === "publish") startGoLive();
    onRequestHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedAction]);

  const renderActions = () => {
    if (!hasStations) return null;

    const actions: React.ReactNode[] = [];

    if (publishAction) {
      actions.push(
        <Button
          key="publish"
          label={publishLabel}
          icon={isConfirming ? "pi pi-check-circle" : "pi pi-bolt"}
          loading={busy}
          disabled={busy}
          onClick={startGoLive}
        />
      );
    }

    if (canResendGoLiveEmail(state)) {
      actions.push(
        <Button
          key="resend-go-live"
          label="Send confirmation email again"
          icon="pi pi-envelope"
          outlined
          loading={busy}
          disabled={busy}
          onClick={confirmResend}
        />
      );
    }

    // Keyed on the charging record rather than the stage: a stage can be moved
    // by hand without any session having happened, and this e-mail asserts one
    // did. Highlighted (filled, not outlined) while the customer has charged but
    // has not been told.
    if (hasFirstCharge(prospect)) {
      actions.push(
        <Button
          key="resend-first-charge"
          label={
            lastFirstChargeAttempt(prospect)
              ? "Send welcome email again"
              : "Send welcome email"
          }
          icon="pi pi-envelope"
          outlined={!firstChargeEmailNeedsAttention(prospect)}
          loading={busy}
          disabled={busy}
          onClick={confirmResendFirstCharge}
        />
      );
    }

    if (canSendConfigEmail(state)) {
      actions.push(
        <Button
          key="send"
          label={
            isConfigEmailSent(state)
              ? "Resend configuration email"
              : "Send configuration email"
          }
          icon="pi pi-envelope"
          loading={busy}
          // CWA-232: the first send is already going out on its own; a stuck one (stale) can be taken over.
          disabled={
            busy ||
            (!isConfigEmailSent(state) &&
              prospect.config_email_dispatch?.status === "PENDING" &&
              !prospect.config_email_dispatch.stale)
          }
          onClick={confirmSend}
        />
      );
    }

    // Reachable again since the welcome-email action moved from a stage check to
    // the charging record: a prospect at "first session charged" that has no
    // record yet offers nothing, and an empty action row reads as a broken
    // screen rather than a finished one.
    if (!actions.length) {
      return (
        <Message
          severity="info"
          className="w-full"
          text={`Nothing to do here at the moment — this prospect is at "${stateLabel(
            state
          )}".`}
        />
      );
    }

    return (
      <div className="flex flex-wrap gap-2 align-items-center">
        {actions}
      </div>
    );
  };

  // Shown from "contract signed" onwards. Placed after the hooks so none of them
  // run conditionally.
  if (!isStationConfigStage(state)) return null;

  return (
    <div
      className="mb-4 onboarding-section"
      data-testid="station-config-panel"
    >
      <ConfirmDialog />
      {reviewed?.state === "loaded" && reviewed.record && (
        <ProvisioningDialog
          record={reviewed.record}
          // CWA-231: approval runs through "Set up in eRound + OCPP", so the customer is promoted first.
          approvable={false}
          visible
          onHide={() => setReviewing(null)}
          onChanged={provisioning.reload}
        />
      )}

      <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <h3 className="m-0">Station configuration</h3>
        <div className="flex align-items-center gap-2">
          <Tag value={stateLabel(state)} />
          {hasStations &&
            (allCredentialsSaved ? (
              <Tag severity="success" value="Ready to send" />
            ) : (
              <Tag severity="warning" value="Not ready" />
            ))}
        </div>
      </div>

      {error && (
        <Message
          severity="error"
          className="w-full mb-3"
          text="The station information could not be loaded."
        />
      )}

      {/* Multi-WALLBOX orders get a manual review before anything is sent. This
          used to key on the charge point count, which fired on a single
          two-socket wallbox — exactly the case CWA-154 says needs no review. The
          backend flag is still honoured: it clears once station IDs are
          assigned, so on its own it would never fire here. */}
      {needsWallboxClarification(prospect) && (
        <Message
          severity="warn"
          className="w-full mb-3"
          text={`This order has ${stationCountLabel(
            stationCounts(prospect)
          )} — check the setup manually before sending the configuration.`}
        />
      )}

      {!loading && !error && !hasStations && (
        <Message
          severity="info"
          className="w-full mb-3"
          text="No station IDs have been assigned yet. Assign them first — the access data is then created automatically."
        />
      )}

      {hasStations && (
        <>
          <Message
            severity={allCredentialsSaved ? "success" : "warn"}
            className="w-full mb-3"
            text={
              allCredentialsSaved
                ? "Every station has its access data."
                : "Some stations are still missing their access data, so the configuration cannot be sent yet."
            }
          />

          {/* Readiness comes from an hourly sync, so it is stated as elapsed
              time and never as a live fact: "ready for 3 hours", not "available
              now" (FR-020). The progress is what separates a customer stuck on
              their second wallbox from one who has not started. */}
          {readinessLabel(prospect.station_readiness) && (
            <div className="flex align-items-center gap-2 flex-wrap mb-3">
              <span className="text-500 text-sm">Stations reported:</span>
              <Tag
                severity={readinessSeverity(prospect.station_readiness)}
                value={readinessLabel(prospect.station_readiness) as string}
              />
              {availabilityProgress(prospect) && (
                <span className="text-500 text-sm">
                  {availabilityProgress(prospect)?.label}
                </span>
              )}
              {readyDuration(prospect.ready_to_publish_since) && (
                <span className="text-500 text-sm">
                  {readyDuration(prospect.ready_to_publish_since)}
                </span>
              )}
              <span className="text-500 text-sm">
                — checked periodically, so this can lag behind the station.
              </span>
            </div>
          )}

          {/* Said once for the prospect rather than repeated on every row
              (FR-011). The count is what an agent acts on: each of these needs
              its access data re-saved before the wallbox can connect. */}
          {staleCount > 0 && (
            <Message
              severity="warn"
              className="w-full mb-3"
              text={`${staleCount} ${
                staleCount === 1 ? "station is" : "stations are"
              } stored under an identity eRound will not match, so ${
                staleCount === 1 ? "it cannot" : "they cannot"
              } connect. Re-save the access data on each to correct ${
                staleCount === 1 ? "it" : "them"
              } — this issues new access data, so the customer needs the configuration email again.`}
            />
          )}

          <DataTable
            value={stations}
            loading={loading}
            dataKey="station_id"
            className="mb-3"
            emptyMessage="No stations yet."
            expandedRows={expandedRows}
            onRowToggle={(e) => setExpandedRows(e.data)}
            rowExpansionTemplate={(station: ProspectStation) =>
              activityBody(station)
            }
          >
            {/* PrimeReact's own toggler label comes from a locale key this app
                never sets, so it is named here explicitly. */}
            <Column
              expander
              style={{ width: "3rem" }}
              pt={{ rowToggler: { "aria-label": "Show station activity" } }}
            />
            {/* One identity, not two: eRound holds a single technical name per
                station, which is both its name and what the hardware presents
                when it connects (CWA-154 finding 4). Where the stored value
                disagrees, both are shown — the station's own ID, and what is
                actually stored — so an agent can see the mismatch without a
                second column. */}
            <Column header="Station ID" body={identityBody} />
            <Column header="Status" body={lifecycleBody} />
            {provisioningShown && (
              <Column
                header="eRound"
                body={(s: ProspectStation) => (
                  <ProvisioningCell
                    stationId={s.station_id}
                    entry={provisioning.byStation[s.station_id]}
                    onReview={() => setReviewing(s.station_id)}
                    onChanged={provisioning.reload}
                  />
                )}
              />
            )}
            {/* The key is what the row is opened for, so it is shown here
                rather than behind the expander — one press per station, still
                on demand and still audited. */}
            <Column
              header="Access key"
              body={accessKeyBody}
              style={{ minWidth: "12rem" }}
            />
            <Column
              header="Recorded on"
              body={(s: ProspectStation) => formatDate(s.credentials_saved_at)}
            />
            <Column
              header="Recorded by"
              body={(s: ProspectStation) => formatActor(s.credentials_saved_by)}
            />
            <Column
              header="Went live"
              body={(s: ProspectStation) => formatDate(s.went_live_at)}
            />
            <Column header="" body={actionsBody} style={{ width: "8rem" }} />
          </DataTable>

          {/* Said once for the table rather than under every key. */}
          <div className="mb-3 text-sm text-500">
            Enter each access key in eRound together with its station ID. Showing
            a key is recorded on the customer's history.
          </div>

          {vendor && (
            <div className="mb-3 text-sm text-500">
              Setup files for this customer come from the{" "}
              <span className="font-medium">{vendor.vendor_name}</span>{" "}
              configuration
              {isGenericFallback
                ? ` — "${prospect.wallbox_manufacturer}" is not in the catalogue, so the generic one is used.`
                : "."}
            </div>
          )}
        </>
      )}

      {renderActions()}

      {/* The first charging session and what the customer has been told about
          it. Every value here comes from the charging record or the recorded
          send attempts, so nothing is inferred from the prospect's stage. */}
      {hasFirstCharge(prospect) && (
        <div
          className="mt-3 onboarding-subsection"
          data-testid="first-charge-card"
        >
          <h4 className="mt-0 mb-2">First charging session</h4>

          <div className="grid text-sm">
            <div className="col-12 md:col-6 mb-2">
              <div className="text-500">Charged on</div>
              <div className="font-medium">
                {formatDate(prospect.first_cdr_date)}
              </div>
            </div>
            <div className="col-12 md:col-6 mb-2">
              <div className="text-500">Station</div>
              <div className="font-medium">
                {prospect.first_cdr_station_id ?? "—"}
              </div>
            </div>
          </div>

          {firstChargeEmailNeedsAttention(prospect) && (
            <Message
              severity="warn"
              className="w-full mb-2"
              text={
                lastFirstChargeAttempt(prospect)
                  ? "The last welcome email failed — the customer has not been told their station is earning."
                  : "No welcome email has gone out yet for this session."
              }
            />
          )}

          {!!(prospect.first_charge_email_attempts ?? []).length && (
            <>
              <div className="text-500 text-sm mb-1">Welcome email</div>
              <ul className="list-none p-0 m-0">
                {(prospect.first_charge_email_attempts ?? []).map(
                  (attempt, i) => (
                    <li
                      key={`${attempt.sent_at}-${i}`}
                      className="mb-1 flex align-items-center gap-2 text-sm"
                    >
                      <Tag
                        severity={attemptOutcomeSeverity(attempt.outcome)}
                        value={attemptOutcomeLabel(attempt.outcome)}
                      />
                      <span className="text-500">
                        {formatDate(attempt.sent_at)} ·{" "}
                        {attemptSourceLabel(attempt.source)}
                      </span>
                    </li>
                  )
                )}
              </ul>
            </>
          )}
        </div>
      )}

      {lastGoLiveAttempt && (
        <div className="mt-3 text-500 text-sm">
          Last go-live attempt: {formatDate(lastGoLiveAttempt.attempted_at)} ·{" "}
          {lastGoLiveAttempt.actor}
        </div>
      )}

      <StationCredentialsDialog
        station={editing}
        onHide={() => setEditing(null)}
        onSubmit={(payload) =>
          saveCredentials(editing?.station_id ?? "", payload)
        }
      />

      {emailRequest && (
        <EmailPreviewDialog
          visible={emailOpen}
          prospectId={prospect.id}
          type={emailRequest.type}
          header={emailRequest.header}
          intro={emailRequest.intro}
          sendLabel={emailRequest.sendLabel}
          sendIcon={emailRequest.sendIcon}
          onSend={emailRequest.onSend}
          onHide={() => setEmailOpen(false)}
          errorMessage={emailRequest.errorMessage}
          blockOnErrors={emailRequest.blockOnErrors}
        />
      )}

      <GoLiveDialog
        visible={goLiveOpen}
        prospectId={prospect.id}
        stations={stations}
        customerEmail={prospect.email}
        confirmLabel={isConfirming ? "Confirm" : undefined}
        privateInEround={privateInEround}
        onHide={() => setGoLiveOpen(false)}
        onConfirm={(stationIds) =>
          markLive(stationIds, false, publishAction ?? "go-live")
        }
      />
    </div>
  );
};

export default StationConfigPanel;
